export interface HreflangLink {
	/** Plain string attrs, so the links drop into a unhead `link` array as-is. */
	[attribute: string]: string;
	key: string;
	rel: "alternate";
	hreflang: string;
	href: string;
}

interface BuildHreflangLinksInput {
	locales: ReadonlyArray<{ code: string }>;
	/** From `resolveHreflangs` — unique per locale; a missing code is omitted. */
	hreflangByCode: Readonly<Record<string, string>>;
	defaultLocale: string;
	/** Absolute URL of the current page in `code`, or undefined when it has none. */
	hrefFor: (code: string) => string | undefined;
}

/**
 * Alternate links keyed on the resolved hreflang, not on the locale's
 * `language`/`iso`: markets can share a Norce culture (`us` and `global` on
 * en-US), and keying on it lets the later one silently replace the earlier.
 */
export const buildHreflangLinks = ({
	locales,
	hreflangByCode,
	defaultLocale,
	hrefFor
}: BuildHreflangLinksInput): HreflangLink[] =>
	locales.flatMap(({ code }) => {
		const hreflang = hreflangByCode[code];
		const href = hreflang ? hrefFor(code) : undefined;
		if (!hreflang || !href) return [];

		const alternate: HreflangLink = {
			key: `i18n-alt-${hreflang}`,
			rel: "alternate",
			hreflang,
			href
		};
		return code === defaultLocale
			? [
					{ key: "i18n-xd", rel: "alternate", hreflang: "x-default", href },
					alternate
				]
			: [alternate];
	});
